import { groupIndustryNavigation, workAreaCategoryLabel } from '@vivaporta/work-areas';
import { html } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { brandAssetRepository } from '@vivaporta/brand';
import type { LandingContent, LandingLocale, LandingNavItem } from '../../shared/content/landingContentTypes.js';
import type { StaticPageAudience } from '../../shared/content/staticPageContent.js';
import { mobileNavigationContent } from '../../shared/content/mobileNavigationContent.js';
import { languageToggle } from '../components/languageToggle.js';
import { navigationIndustryIcon } from './navigationIndustryIcon.js';
import { editorNodeClass, editorNodeClick, editorNodePointerDown, editorNodeStyle, type LandingEditorRenderOptions } from '../editor/landingEditorRender.js';

const closing = new WeakMap<HTMLDialogElement, Animation>();
const menuSelector = '.vp-mobile-mega';
const syncToggle = (dialog: HTMLDialogElement, open: boolean) =>
  dialog.closest('.vp-navigation')?.querySelector('.vp-mobile-toggle')?.setAttribute('aria-expanded', String(open));
const unlock = (dialog: HTMLDialogElement) => {
  syncToggle(dialog, false);
  if (!document.querySelector('.vp-contact-modal[open]')) document.querySelector('.vp-page')?.classList.remove('vp-scroll-locked');
};
const finishClose = (dialog: HTMLDialogElement) => {
  closing.get(dialog)?.cancel(); closing.delete(dialog);
  if (dialog.open) dialog.close();
  unlock(dialog);
};
export const closeMobileNavigation = (immediate = true) => {
  document.querySelectorAll<HTMLDialogElement>(`${menuSelector}[open]`).forEach(dialog => {
    if (immediate || matchMedia('(prefers-reduced-motion: reduce)').matches) { finishClose(dialog); return; }
    if (closing.has(dialog)) return;
    const animation = dialog.animate([{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-12px)' }], { duration: 170, easing: 'ease-in' });
    closing.set(dialog, animation); animation.onfinish = () => finishClose(dialog);
  });
};
const open = (dialog: HTMLDialogElement, editor = false) => {
  closing.get(dialog)?.cancel(); closing.delete(dialog);
  if (!dialog.open) {
    if (!editor) dialog.querySelectorAll<HTMLDetailsElement>('.vp-mobile-industry-disclosure').forEach(section => { section.open = false; });
    if (editor) dialog.setAttribute('open', '');
    else dialog.showModal();
  }
  syncToggle(dialog, true);
  if (!editor) document.querySelector('.vp-page')?.classList.add('vp-scroll-locked');
};

interface MobileNavigationOptions {
  content: LandingContent;
  locale: LandingLocale;
  audience: StaticPageAudience;
  industryItems: LandingNavItem[];
  currentPath: string;
  audienceHref: string;
  applicantLabel: string;
  companyLabel: string;
  onAudienceChange: (event: MouseEvent) => void;
  onLanguageChange: (locale: LandingLocale) => void;
  onLink: (event: MouseEvent, href: string) => void;
  hrefFor: (href: string) => string;
  onCompanyContact: (event: MouseEvent) => void;
  editor?: LandingEditorRenderOptions;
}

/** A native modal on the website; an ordinary canvas element while editing. */
export const mobileNavigation = (options: MobileNavigationOptions) => {
  const { content, locale, audience, editor } = options, root = 'navigation.mobile';
  const menu = content.mobileNavigation ?? mobileNavigationContent(locale);
  const isEditor = editor?.mode === 'editor';
  const ui = locale === 'tr' ? { open: 'Menüyü aç', close: 'Menüyü kapat', language: 'Dil', navigation: 'Ana menü' }
    : locale === 'en' ? { open: 'Open menu', close: 'Close menu', language: 'Language', navigation: 'Main navigation' }
    : { open: 'Menü öffnen', close: 'Menü schließen', language: 'Sprache', navigation: 'Hauptnavigation' };
  let bound: HTMLDialogElement | undefined, controller: AbortController | undefined;
  const bind = (node: Element | undefined) => {
    controller?.abort();
    if (!node) { if (bound?.open) unlock(bound); bound = undefined; return; }
    if (!(node instanceof HTMLDialogElement)) return;
    bound = node; controller = new AbortController();
    window.addEventListener('resize', () => {
      if (!isEditor && window.innerWidth > 1100) finishClose(node);
    }, { signal: controller.signal });
    if (node.open) open(node, isEditor);
    else if (isEditor && editor.selectedElementId?.startsWith(root + '.'))
      queueMicrotask(() => { if (node.isConnected) open(node, true); });
  };
  const select = (event: MouseEvent, id: string) => {
    if (!isEditor) return false;
    editorNodeClick(editor, id, event); return true;
  };
  const field = (value: string, id: string) => html`<span
    class=${editorNodeClass(editor, id)} style=${editorNodeStyle(editor, id)}
    @pointerdown=${(e: PointerEvent) => editorNodePointerDown(editor, id, e)}
    @click=${(e: MouseEvent) => editorNodeClick(editor, id, e)}>${value}</span>`;
  const region = (suffix: string, className: string, children: unknown) => html`<div
    class=${editorNodeClass(editor, `${root}.${suffix}`, className)} style=${editorNodeStyle(editor, `${root}.${suffix}`)}
    @pointerdown=${(e: PointerEvent) => editorNodePointerDown(editor, `${root}.${suffix}`, e)}
    @click=${(e: MouseEvent) => editorNodeClick(editor, `${root}.${suffix}`, e)}>${children}</div>`;
  const link = (item: LandingNavItem, id: string, industry = false) => {
    const external = /^https?:/i.test(item.href);
    return html`<a class=${editorNodeClass(editor, id, industry ? 'vp-mobile-industry-link' : 'vp-mobile-mega-link')}
      style=${editorNodeStyle(editor, id)} href=${options.hrefFor(item.href)}
      target=${external ? '_blank' : '_self'} rel=${external ? 'noopener noreferrer' : ''}
      aria-current=${item.href === options.currentPath ? 'page' : 'false'}
      @pointerdown=${(e: PointerEvent) => editorNodePointerDown(editor, id, e)}
      @click=${(e: MouseEvent) => { if (select(e, id)) return; closeMobileNavigation(); options.onLink(e, item.href); }}>
      ${industry ? html`<span class="vp-industry-menu-icon" aria-hidden="true"><span class=${navigationIndustryIcon(item.href)}></span></span>` : ''}
      ${field(item.label, id + '.label')}
      ${industry ? '' : html`<span class="icon-[material-symbols--arrow-outward-rounded]" aria-hidden="true"></span>`}
    </a>`;
  };
  const close = (event: Event) => {
    event.preventDefault(); event.stopPropagation();
    if (isEditor) editor.onElementSelect?.('navigation.container');
    closeMobileNavigation(isEditor);
  };
  const keydown = (event: KeyboardEvent) => {
    if (isEditor && event.key === 'Escape' && !(event.target as HTMLElement).closest('[contenteditable]')) close(event);
    if (isEditor || event.key !== 'Tab') return;
    const dialog = event.currentTarget as HTMLDialogElement;
    const controls = [...dialog.querySelectorAll<HTMLElement>('a[href], button, summary, input, select, textarea, [tabindex]')]
      .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length > 0);
    const first = controls[0], last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first && last) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last && first) { event.preventDefault(); first.focus(); }
  };
  const actionKey = audience === 'company' ? 'companyAction' : 'applicantAction', action = menu[actionKey];
  const actionClick = (event: MouseEvent) => {
    if (select(event, `${root}.${actionKey}.label`)) return;
    closeMobileNavigation();
    if (audience === 'company' && action.href === '/kontakt') options.onCompanyContact(event);
    else options.onLink(event, action.href);
  };
  const journeyKey = audience === 'company' ? 'companyLinks' : 'applicantLinks';
  const journeyTitleKey = audience === 'company' ? 'companyTitle' : 'applicantTitle';
  return html`
    <div class="vp-mobile-menu">
      <button type="button" class="vp-mobile-toggle" aria-label=${ui.open} aria-haspopup="dialog" aria-controls="vp-mobile-navigation" aria-expanded="false"
        @pointerdown=${(event: PointerEvent) => event.stopPropagation()}
        @click=${(event: MouseEvent) => {
          event.preventDefault(); event.stopPropagation();
          const dialog = (event.currentTarget as HTMLElement).closest('.vp-navigation')?.querySelector<HTMLDialogElement>(menuSelector);
          if (dialog) open(dialog, isEditor);
          if (isEditor) editor.onElementSelect?.(`${root}.container`);
        }}><span class="icon-[material-symbols--menu-rounded]" aria-hidden="true"></span></button>
    </div>
    <dialog ${ref(bind)} id="vp-mobile-navigation" class=${editorNodeClass(editor, `${root}.container`, 'vp-mobile-panel vp-mobile-mega')}
      style=${editorNodeStyle(editor, `${root}.container`)} aria-label=${ui.navigation} aria-modal=${isEditor ? 'false' : 'true'}
      @cancel=${close} @close=${(event: Event) => unlock(event.currentTarget as HTMLDialogElement)}
      @keydown=${keydown}
      @pointerdown=${(e: PointerEvent) => editorNodePointerDown(editor, `${root}.container`, e)}
      @click=${(e: MouseEvent) => editorNodeClick(editor, `${root}.container`, e)}>
      <div class=${editorNodeClass(editor, `${root}.header`, 'vp-mobile-mega-header')} style=${editorNodeStyle(editor, `${root}.header`)}
        @pointerdown=${(e: PointerEvent) => editorNodePointerDown(editor, `${root}.header`, e)} @click=${(e: MouseEvent) => editorNodeClick(editor, `${root}.header`, e)}>
        <a class="vp-mobile-mega-brand" href=${options.hrefFor('/')} aria-label="Viva Porta"
          @click=${(event: MouseEvent) => { if (select(event, `${root}.logo`)) return; closeMobileNavigation(); options.onLink(event, '/'); }}>
          <img class=${editorNodeClass(editor, `${root}.logo`)} style=${editorNodeStyle(editor, `${root}.logo`)}
            src=${content.brandLogo || brandAssetRepository.navbarLogoDataUri} alt="Viva Porta"
            @pointerdown=${(e: PointerEvent) => editorNodePointerDown(editor, `${root}.logo`, e)} />
        </a>
        <button type="button" class="vp-mobile-mega-close" aria-label=${ui.close} autofocus
          @pointerdown=${(e: PointerEvent) => e.stopPropagation()} @click=${close}><span class="icon-[material-symbols--close-rounded]" aria-hidden="true"></span></button>
      </div>
      <nav class=${editorNodeClass(editor, `${root}.body`, 'vp-mobile-mega-scroll')} style=${editorNodeStyle(editor, `${root}.body`)} aria-label=${ui.navigation}
        @pointerdown=${(e: PointerEvent) => editorNodePointerDown(editor, `${root}.body`, e)} @click=${(e: MouseEvent) => editorNodeClick(editor, `${root}.body`, e)}>
        <div class="vp-mobile-mega-content">
          <div class="vp-mobile-audience-switch" aria-label=${locale === 'tr' ? 'Hedef kitle' : locale === 'en' ? 'Audience' : 'Zielgruppe'}>
            ${(['applicant', 'company'] as const).map(value => html`<a class=${audience === value ? 'is-active' : ''} href=${options.audienceHref}
              aria-current=${audience === value ? 'true' : 'false'} data-audience=${value}
              @pointerdown=${(e: PointerEvent) => e.stopPropagation()} @click=${options.onAudienceChange}>
                <span class=${value === 'company' ? 'icon-[material-symbols--business-center]' : 'icon-[material-symbols--person-check]'} aria-hidden="true"></span>
                <span>${value === 'company' ? options.companyLabel : options.applicantLabel}</span>
              </a>`)}
          </div>
          ${region('primary', 'vp-mobile-mega-section vp-mobile-mega-primary', html`
            <h2>${field(menu.title, `${root}.title`)}</h2>
            ${region('primaryGrid', 'vp-mobile-mega-primary-links', menu.links.map((item, index) => link(item, `${root}.links.${index}`)))}`)}
          ${region('industries', 'vp-mobile-mega-section vp-mobile-mega-industries', html`
            <details class="vp-mobile-industry-disclosure" ?open=${isEditor}>
              <summary class="vp-mobile-industry-toggle">
                <h2>${field(menu.industriesTitle, `${root}.industriesTitle`)}<span class="vp-mobile-industry-chevron icon-[material-symbols--keyboard-arrow-down-rounded]" aria-hidden="true"></span></h2>
              </summary>
              ${region('industryGrid', 'vp-mobile-industry-groups', html`
                ${groupIndustryNavigation(options.industryItems).groups.filter(group=>group.parent || group.children.length).map(group=>html`<section class="vp-mobile-industry-group" data-industry-category=${group.category}>
                  <div class="vp-mobile-industry-group-heading">${group.parent ? link(group.parent.item,`${root}.industry.${group.parent.index}`,true) : html`<strong>${workAreaCategoryLabel(group.category,locale)}</strong>`}</div>
                  <div class="vp-mobile-industry-grid">${group.children.map(({item,index})=>link(item,`${root}.industry.${index}`,true))}</div>
                </section>`)}
                ${groupIndustryNavigation(options.industryItems).ungrouped.map(({item,index})=>link(item,`${root}.industry.${index}`,true))}
              `)}
            </details>`)}
          ${region('journey', 'vp-mobile-mega-section vp-mobile-mega-journey', html`
            <h2>${field(menu[journeyTitleKey], `${root}.${journeyTitleKey}`)}</h2>
            ${region('journeyGrid', 'vp-mobile-mega-journey-links', menu[journeyKey].map((item, index) => link(item, `${root}.${journeyKey}.${index}`)))}`)}
          ${region('meta', 'vp-mobile-mega-meta', menu.footerLinks.map((item, index) => link(item, `${root}.footerLinks.${index}`)))}
        </div>
      </nav>
      <div class=${editorNodeClass(editor, `${root}.footer`, 'vp-mobile-mega-footer')} style=${editorNodeStyle(editor, `${root}.footer`)}
        @pointerdown=${(e: PointerEvent) => editorNodePointerDown(editor, `${root}.footer`, e)} @click=${(e: MouseEvent) => editorNodeClick(editor, `${root}.footer`, e)}>
        ${audience === 'company' && action.href === '/kontakt'
          ? html`<button type="button" class="vp-mobile-mega-action" @click=${actionClick}>${field(action.label, `${root}.${actionKey}.label`)}<span class="icon-[material-symbols--arrow-outward-rounded]" aria-hidden="true"></span></button>`
          : html`<a class="vp-mobile-mega-action" href=${options.hrefFor(action.href)} @click=${actionClick}>${field(action.label, `${root}.${actionKey}.label`)}<span class="icon-[material-symbols--arrow-outward-rounded]" aria-hidden="true"></span></a>`}
        <div class="vp-mobile-mega-language"><span>${ui.language}</span>${languageToggle({ currentLocale: locale, onChange: options.onLanguageChange })}</div>
      </div>
    </dialog>`;
};
